import { useClientId, useCopyPasteStyles, useDeviceType } from "@blocks/hooks";
import { withSelect } from "@wordpress/data";
import { Fragment } from "@wordpress/element";
import { EditProps } from "blocks/types";
import classnames from "classnames";
import React, { useEffect } from "react";
import IconRenderer from "../../components/common/IconRenderer";
import { useBlockStyle } from "../../hooks";
import "../assets/sass/blocks/grid-module/style.scss";
import InspectorControls from "./components/InspectorControls";
import { cn } from "../../utils";
import { Icon } from "../../components";

interface Props extends EditProps<any> {
	categories: Array<any>;
	posts: Array<any>;
	tags: Array<any>;
	numberOfPosts: number;
	page: number;
	author: Array<any>;
}

const Edit: React.ComponentType<Props> = (props) => {
	const {
		posts,
		attributes: { icon, label, hideOnDesktop, enableArrows, category },
		setAttributes,
		categories,
	} = props;

	const { clientId } = useClientId(props);
	const { deviceType } = useDeviceType();
	const { CopyPasterStyleBlockControl } = useCopyPasteStyles();
	const { Style } = useBlockStyle({
		blockName: "news-ticker",
		clientId,
		attributes: props.attributes,
		deviceType,
	});

	useEffect(() => {
		if (!enableArrows) return;
		const ticker = document.querySelector(`.mzb-news-ticker-${clientId}`);
		if (!ticker) return;

		let currentIndex = 0;
		const items = ticker.querySelectorAll(".mzb-news-ticker-list li");
		const totalPosts = items.length;

		// Function to update visible items
		const updateDisplay = (index: number) => {
			items.forEach((item, idx) => {
				(item as HTMLElement).style.display =
					idx === index ? "block" : "none";
			});
		};

		// Initial display
		updateDisplay(currentIndex);

		// Handle navigation clicks
		const handleNavClick = (e: Event) => {
			const target = e.target as HTMLElement;
			if (!target.classList.contains("mzb-news-ticker-nav-btn")) return;
			e.preventDefault();

			const action = target.getAttribute("data-action");

			if (action === "prev") {
				currentIndex =
					currentIndex === 0 ? totalPosts - 1 : currentIndex - 1;
			} else if (action === "next") {
				currentIndex =
					currentIndex === totalPosts - 1 ? 0 : currentIndex + 1;
			}

			updateDisplay(currentIndex);
		};

		ticker.addEventListener("click", handleNavClick);

		// Cleanup
		return () => {
			ticker.removeEventListener("click", handleNavClick);
		};
	}, [clientId, posts, enableArrows]);

	const classNames = classnames(
		`mzb-news-ticker mzb-news-ticker-${clientId}`,
		hideOnDesktop && "magazine-blocks-hide-on-desktop"
	);

	return (
		<Fragment>
			<InspectorControls
				attributes={props.attributes}
				setAttributes={setAttributes}
				categories={categories}
			/>
			<CopyPasterStyleBlockControl withBlockControls />
			<Style />
			<div className={classNames}>
				<span className="mzb-weather">
					<IconRenderer type="newsTickerIcon" name={icon} size={24} />
				</span>
				<span className="mzb-heading">{label}</span>
				<div
					className={cn(
						`mzb-news-ticker-box`,
						enableArrows && "mzb-news-ticker-arrows"
					)}
					data-total-posts={posts?.length || 0}
				>
					{enableArrows && (
						<a
							href="#"
							className="mzb-news-ticker-nav-btn prev"
							data-action="prev"
							aria-label="Previous"
						>
							<Icon name="chevronLeft" />
						</a>
					)}
					<ul className={`mzb-news-ticker-list`}>
						{(posts || []).map((post, idx) => {
							return (
								<li key={idx}>
									<a>{post.title.rendered}</a>
								</li>
							);
						})}
					</ul>
					{enableArrows && (
						<a
							href="#"
							className="mzb-news-ticker-nav-btn next"
							data-action="next"
							aria-label="Next"
						>
							<Icon name="chevronRight" />
						</a>
					)}
				</div>
			</div>
		</Fragment>
	);
};

//@ts-ignore
export default withSelect((select, props) => {
	const { getEntityRecords } = select("core");
	const {
		attributes: { category, order },
	} = props;
	const query = {
		order,
	};
	return {
		posts: getEntityRecords("postType", "post", {
			...query,
			per_page: 4,
			categories: "all" === category ? undefined : parseInt(category),
		}),
		numberOfPosts:
			getEntityRecords("postType", "post", {
				per_page: -1,
				categories: "all" === category ? undefined : parseInt(category),
			})?.length || 0,
		categories:
			getEntityRecords("taxonomy", "category", { per_page: -1 }) || [],
	};
})(Edit);
